import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '~/components/multi-select';
import { DateInput } from '~/components/date-input';
import { cn } from '@/lib/utils';
import { useCategoriesList } from '~/hooks/use-categories';
import { useStatusesList } from '~/hooks/use-statuses';
import { useManufacturersList } from '~/hooks/use-manufacturers';
import { useWarehousesList } from '~/hooks/use-warehouses';
import { useProductAvailabilityOptions } from '~/hooks/use-product-lookups';
import { MetricTypesLeaf } from './metric-types-leaf';
import {
	ALL_FILTERS,
	COMPOUND_FILTERS,
	FILTER_AND,
	FILTER_NOT,
	FILTER_OR,
	LEAF_FILTERS,
	filterDef,
	PRODUCT_SEARCH_FIELDS,
	type FilterNodeValue,
	type IdsEntity,
	type LeafDef
} from '~/lib/filters/registry';
import { OP_TYPE_RANGE, operandById, orderedValueOperands, searchTextOperands } from '~/lib/filters/operands';
import { OperandSelect } from './operand-select';

/* ------------------------------------------------------------------ operand + scalar editors */

/** Ordered value/range editor shared by the numeric and date leaves. */
function OrderedLeaf({ value, onChange, def }: { value: FilterNodeValue; onChange: (v: FilterNodeValue) => void; def: LeafDef }) {
	const { t } = useTranslation();
	const isDate = def.kind === 'ordered-date';
	const operand = operandById(value.op);
	const isRange = operand?.type === OP_TYPE_RANGE;

	// Switching operand family clears the now-irrelevant value/from/to (legacy reducer parity).
	const setOp = (op: string) => {
		const { value: _v, from: _f, to: _t, ...rest } = value;
		onChange({ ...rest, op });
	};
	const setField = (field: 'value' | 'from' | 'to', v: unknown) => onChange({ ...value, [field]: v });

	// Plain render function (NOT a component) so the inputs keep their identity across renders
	// and don't lose focus on each keystroke.
	const scalarInput = (field: 'value' | 'from' | 'to', placeholder: string) =>
		isDate ? (
			<DateInput
				key={field}
				value={value[field] ? new Date(value[field] as string) : null}
				onChange={d => setField(field, d ? d.toISOString() : null)}
				placeholder={placeholder}
			/>
		) : (
			<Input
				key={field}
				type="number"
				className="h-9 w-32"
				min={def.min}
				max={def.max}
				placeholder={placeholder}
				value={(value[field] as number | undefined) ?? ''}
				onChange={e => setField(field, e.target.value === '' ? null : Number(e.target.value))}
			/>
		);

	return (
		<div className="flex flex-wrap items-center gap-2">
			<OperandSelect value={value.op} onChange={setOp} operands={orderedValueOperands} />
			{value.op &&
				(isRange ? (
					<>
						{scalarInput('from', t('catalog.filters.from'))}
						{scalarInput('to', t('catalog.filters.to'))}
					</>
				) : (
					scalarInput('value', t('catalog.filters.value'))
				))}
			{def.supportsLimit && (
				<label className="flex items-center gap-1.5 text-xs text-foreground-muted">
					<input
						type="checkbox"
						checked={value.limit !== undefined}
						onChange={e => {
							if (e.target.checked) onChange({ ...value, limit: null });
							else {
								const { limit: _l, ...rest } = value;
								onChange(rest);
							}
						}}
					/>
					{t('catalog.filters.limit')}
					{value.limit !== undefined && (
						<Input
							type="number"
							className="h-8 w-24"
							min={1}
							value={value.limit ?? ''}
							onChange={e => onChange({ ...value, limit: e.target.value === '' ? null : Number(e.target.value) })}
						/>
					)}
				</label>
			)}
		</div>
	);
}

function TopSellingLeaf({ value, onChange }: { value: FilterNodeValue; onChange: (v: FilterNodeValue) => void }) {
	const { t } = useTranslation();
	return (
		<div className="flex flex-wrap items-center gap-2">
			<Input
				type="number"
				className="h-9 w-32"
				min={1}
				placeholder={t('catalog.filters.topSellingLimit')}
				value={value.Limit ?? ''}
				onChange={e => onChange({ ...value, Limit: e.target.value === '' ? undefined : Number(e.target.value) })}
			/>
			<Input
				type="number"
				className="h-9 w-32"
				min={1}
				placeholder={t('catalog.filters.dayWindow')}
				value={value.DayWindow ?? ''}
				onChange={e => onChange({ ...value, DayWindow: e.target.value === '' ? undefined : Number(e.target.value) })}
			/>
		</div>
	);
}

function SearchTermLeaf({ value, onChange }: { value: FilterNodeValue; onChange: (v: FilterNodeValue) => void }) {
	const { t } = useTranslation();
	const fieldOpts = PRODUCT_SEARCH_FIELDS.map(f => ({ value: f }));
	const selectedFields = (value.fields ?? []).map(f => ({ value: f }));
	// The API rejects a term without an operand (BA-7695); flag it here before the save round-trip.
	const operandMissing = !!value.term?.trim() && !value.op;
	return (
		<div className="flex flex-wrap items-center gap-2">
			<div className="min-w-52 flex-1">
				<MultiSelect
					value={selectedFields}
					onChange={next => onChange({ ...value, fields: next.map(o => o.value) })}
					options={fieldOpts}
					getKey={o => o.value}
					getLabel={o => o.value}
					placeholder={t('catalog.filters.searchFields')}
				/>
			</div>
			<OperandSelect
				value={value.op}
				onChange={op => onChange({ ...value, op })}
				operands={searchTextOperands}
				invalid={operandMissing}
			/>
			<Input
				className="h-9 w-48"
				placeholder={t('catalog.filters.term')}
				value={value.term ?? ''}
				onChange={e => onChange({ ...value, term: e.target.value })}
			/>
			{operandMissing && <span className="text-xs text-destructive">{t('catalog.filters.operandRequired')}</span>}
		</div>
	);
}

interface IdOption {
	id: number;
	nm: string;
}

const toIdOptions = (rows: { id?: number | null; nm?: string | null }[] | undefined): IdOption[] =>
	(rows ?? []).filter(r => r.id != null).map(r => ({ id: r.id as number, nm: r.nm ?? '' }));

/** Entity-id multiselect leaf (inCategories / hasStatuses / hasManufacturers / hasAvailabilities / hasAvailableStock). */
function IdsLeaf({ value, onChange, options }: { value: FilterNodeValue; onChange: (v: FilterNodeValue) => void; options: IdOption[] }) {
	const { t } = useTranslation();
	const ids = value.ids ?? [];
	const selected = options.filter(o => ids.includes(o.id));
	return (
		<div className="min-w-52 flex-1">
			<MultiSelect
				value={selected}
				onChange={next => onChange({ ...value, ids: next.map(o => o.id) })}
				options={options}
				getKey={o => String(o.id)}
				getLabel={o => o.nm}
				placeholder={t('catalog.filters.selectEntities')}
			/>
		</div>
	);
}

// One component per entity so a leaf only fetches its own lookup.
type IdsLeafProps = { value: FilterNodeValue; onChange: (v: FilterNodeValue) => void };
const CategoriesIdsLeaf = (p: IdsLeafProps) => <IdsLeaf {...p} options={toIdOptions(useCategoriesList().data)} />;
const StatusesIdsLeaf = (p: IdsLeafProps) => <IdsLeaf {...p} options={toIdOptions(useStatusesList().data)} />;
const ManufacturersIdsLeaf = (p: IdsLeafProps) => <IdsLeaf {...p} options={toIdOptions(useManufacturersList().data)} />;
const WarehousesIdsLeaf = (p: IdsLeafProps) => <IdsLeaf {...p} options={toIdOptions(useWarehousesList().data)} />;
const AvailabilitiesIdsLeaf = (p: IdsLeafProps) => (
	<IdsLeaf {...p} options={(useProductAvailabilityOptions().data ?? []).map(o => ({ id: o.id, nm: o.label }))} />
);

const IDS_LEAVES: Record<IdsEntity, ComponentType<IdsLeafProps>> = {
	categories: CategoriesIdsLeaf,
	statuses: StatusesIdsLeaf,
	manufacturers: ManufacturersIdsLeaf,
	warehouses: WarehousesIdsLeaf,
	availabilities: AvailabilitiesIdsLeaf
};

function LeafEditor({ value, onChange, def }: { value: FilterNodeValue; onChange: (v: FilterNodeValue) => void; def: LeafDef }) {
	const { t } = useTranslation();
	switch (def.kind) {
		case 'ordered-number':
		case 'ordered-date':
			return <OrderedLeaf value={value} onChange={onChange} def={def} />;
		case 'topSelling':
			return <TopSellingLeaf value={value} onChange={onChange} />;
		case 'searchTerm':
			return <SearchTermLeaf value={value} onChange={onChange} />;
		case 'ids': {
			const EntityLeaf = IDS_LEAVES[def.entity!];
			return <EntityLeaf value={value} onChange={onChange} />;
		}
		case 'metricTypes':
			return <MetricTypesLeaf value={value} onChange={onChange} />;
		default:
			return <span className="text-xs text-foreground-subtle italic">{t('catalog.filters.advancedUnsupported')}</span>;
	}
}

/* ------------------------------------------------------------------ filter-type selector */

function FilterTypeSelect({ value, onChange }: { value?: string; onChange: (type: string) => void }) {
	const { t } = useTranslation();
	return (
		<Select value={value ?? undefined} onValueChange={onChange}>
			<SelectTrigger className="h-9 w-56">
				<SelectValue placeholder={t('catalog.filters.selectFilter')} />
			</SelectTrigger>
			<SelectContent>
				<SelectGroup>
					<SelectLabel>{t('catalog.filters.combine')}</SelectLabel>
					{COMPOUND_FILTERS.map(f => (
						<SelectItem key={f.type} value={f.type}>
							{f.label}
						</SelectItem>
					))}
				</SelectGroup>
				<SelectGroup>
					<SelectLabel>{t('catalog.filters.conditions')}</SelectLabel>
					{LEAF_FILTERS.map(f => (
						<SelectItem key={f.type} value={f.type}>
							{t(`catalog.filters.types.${f.type}`, { defaultValue: f.label })}
						</SelectItem>
					))}
				</SelectGroup>
			</SelectContent>
		</Select>
	);
}

/* ------------------------------------------------------------------ recursive node */

interface NodeProps {
	value: FilterNodeValue;
	onChange: (v: FilterNodeValue) => void;
	onRemove?: () => void;
	depth?: number;
}

/** One node in the criteria tree — a compound group (AND/ANY/NOT) or a leaf condition. */
function FilterNode({ value, onChange, onRemove, depth = 0 }: NodeProps) {
	const { t } = useTranslation();
	const def = filterDef(value?.$type);

	// Type change: wrap into compounds (legacy selectedFilterTypeChanged parity), reset on leaf swap.
	const changeType = (newType: string) => {
		if (!newType || newType === value?.$type) return;
		const target = filterDef(newType);
		const hasType = Boolean(value?.$type);
		if (target && target.compound && !target.unary) {
			onChange({ $type: newType, criteria: hasType ? [value] : [{}] });
		} else if (target && target.compound && target.unary) {
			onChange({ $type: newType, criteria: hasType ? value : {} });
		} else {
			onChange({ $type: newType });
		}
	};

	const isAndOr = def?.type === FILTER_AND || def?.type === FILTER_OR;
	const isNot = def?.type === FILTER_NOT;

	if (isAndOr) {
		const children = Array.isArray(value.criteria) ? value.criteria : [];
		const setChildren = (next: FilterNodeValue[]) => onChange({ $type: value.$type, criteria: next });
		// Clicking the group label unwraps it (replace with the first child, legacy handleClear).
		const unwrap = () => onChange(children[0] ?? {});
		return (
			<div className={cn('rounded-md border border-border bg-elevated/40 p-3', depth > 0 && 'mt-1')}>
				<div className="flex items-center gap-2">
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="h-7 font-semibold"
						onClick={unwrap}
						title={t('catalog.filters.unwrap')}>
						{def?.label}
					</Button>
					{onRemove && (
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="h-7 w-7 text-danger-fg"
							onClick={onRemove}
							aria-label={t('common.remove')}>
							<X size={14} />
						</Button>
					)}
				</div>
				<div className="mt-2 flex flex-col gap-2 border-l-2 border-border pl-4">
					{children.map((child, i) => (
						<FilterNode
							key={i}
							value={child}
							depth={depth + 1}
							onChange={next => setChildren(children.map((c, j) => (j === i ? next : c)))}
							onRemove={() => setChildren(children.filter((_, j) => j !== i))}
						/>
					))}
					<div>
						<Button type="button" variant="outline" size="sm" className="h-7" onClick={() => setChildren([...children, {}])}>
							<Plus size={14} /> {t('catalog.filters.addCondition')}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	if (isNot) {
		const child = (Array.isArray(value.criteria) ? value.criteria[0] : value.criteria) ?? {};
		return (
			<div className={cn('rounded-md border border-border bg-elevated/40 p-3', depth > 0 && 'mt-1')}>
				<div className="flex items-center gap-2">
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="h-7 font-semibold"
						onClick={() => onChange(child)}
						title={t('catalog.filters.unwrap')}>
						{def?.label}
					</Button>
					{onRemove && (
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="h-7 w-7 text-danger-fg"
							onClick={onRemove}
							aria-label={t('common.remove')}>
							<X size={14} />
						</Button>
					)}
				</div>
				<div className="mt-2 border-l-2 border-border pl-4">
					<FilterNode value={child} depth={depth + 1} onChange={next => onChange({ $type: FILTER_NOT, criteria: next })} />
				</div>
			</div>
		);
	}

	// Leaf row: filter-type selector + (when chosen) its value editor.
	return (
		<div className="flex flex-wrap items-center gap-2">
			<FilterTypeSelect value={value?.$type} onChange={changeType} />
			{def && !def.compound && <LeafEditor value={value} onChange={onChange} def={def} />}
			{onRemove && (
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="h-7 w-7 text-danger-fg"
					onClick={onRemove}
					aria-label={t('common.remove')}>
					<X size={14} />
				</Button>
			)}
		</div>
	);
}

/** Root of the dynamic-filter builder. `value` is the persisted Filter JSON ({} when empty). */
export function FilterBuilder({ value, onChange }: { value: FilterNodeValue; onChange: (v: FilterNodeValue) => void }) {
	return <FilterNode value={value ?? {}} onChange={onChange} />;
}

export { ALL_FILTERS };
